<template>
    <v-container class="px-0" fluid>
        <v-card class="card-style px-1" outlined elevation="2">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center align-center">
                <!-- Título de la sección -->
                <p class="text-h5 text-truncate mb-5 mr-1">{{ $t('mySignals.title') }}</p>
                <!-- Ícono de ayuda -->
                <v-tooltip bottom open-on-click max-width="180">
                    <template v-slot:activator="{ on }">
                        <div v-on="on" icon color="grey" class="mb-5 mr-2" size="10">
                            <v-icon size="15">mdi-help-circle</v-icon>
                        </div>
                    </template>
                    <span style="justify-content: center !important">{{ $t('mySignals.help') }}</span>
                </v-tooltip>
                <!-- Botón para mostrar/ocultar los filtros -->
                <v-btn
                    v-if="!loadingFavoriteSignals"
                    class="mb-5"
                    icon
                    @click="toggleFilters">
                    <v-icon>{{ filtersVisible ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-card-title>

            <!-- Filtros -->
            <v-slide-y-transition v-if="!loadingFavoriteSignals">
                <v-card-subtitle v-if="filtersVisible" class="elevation-2 mx-4 mt-2">
                    <v-row class="pt-2">
                        <v-col cols="12" md="4" class="pb-0">
                            <v-text-field
                                v-model="filters.tickerName"
                                :label="$t('mySignals.filter.tickerName')"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-select
                                v-model="filters.sector"
                                :items="getSectors"
                                :label="$t('mySignals.filter.sector')"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-select
                                v-model="filters.status"
                                :items="getStatusTypes"
                                :label="$t('mySignals.filter.status')"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                </v-card-subtitle>
            </v-slide-y-transition>

            <!-- Tabla -->
            <v-skeleton-loader
                v-if="loadingFavoriteSignals"
                type="image"
                class="height-450"
            ></v-skeleton-loader>
            <v-card-text v-else class="mt-4">
                <v-data-table
                    :headers="headers"
                    :items="filteredItems"
                    :sort-by.sync="filters.sortBy"
                    :sort-desc.sync="filters.sortDesc"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        'items-per-page-text': $t('mySignals.table.footer.pageable')
                    }"
                    :items-per-page="(isMobileDevice() ? 5 : 10)"
                    class="material-table elevation-1"
                    dense
                    item-value="id"
                    :no-data-text="$t('mySignals.table.empty')"
                    :custom-sort="tableSortConfiguration" >

                    <!-- Nombre con enlace -->
                    <template v-slot:item.ticker="{ item }">
                        <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                    </template>
                    <template v-slot:item.signalPrice="{ item }">
                        <span>$ {{ item.signalPrice }}</span>
                    </template>
                    <template v-slot:item.statusPrice="{ item }">
                        <div>
                            {{calculateStatusPrice(item)}}%
                            <v-icon v-if="item.previousPrice && item.price > item.previousPrice" color="success">mdi-arrow-top-right</v-icon>
                            <v-icon v-else-if="item.previousPrice && item.price < item.previousPrice" color="error">mdi-arrow-bottom-right</v-icon>
                        </div>
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip link :color="getStatusColor()" class="text-white v-chip-medium">
                            {{ getStatusName(item) }}
                        </v-chip>
                    </template>
                    <template v-slot:item.technical="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.technical)">
                                {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.fundamental="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.fundamental)">
                                {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.strength="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.strength)">
                                {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
        <BasicModal :modalCode="modalCode"></BasicModal>
    </v-container>
  </template>

<script>
    import axios from 'axios';

    import { UtilsMixin } from '@/mixins/utils';

    import BasicModal from '@/components/Modal/BasicModal';

    export default {
        name: 'MySignals',
        mixins: [ 
            UtilsMixin 
        ],
        components: {
            BasicModal
        },
        data() {
            return {
                // Filtros
                filtersVisible: false,
                // Datos de la tabla
                headers: [
                    { text: this.$t('mySignals.table.ticker'), value: "ticker" },
                    { text: this.$t('mySignals.table.sector'), value: "sector" },
                    { text: this.$t('mySignals.table.signalPrice'), value: "signalPrice" },
                    { text: this.$t('mySignals.table.statusPrice'), value: "statusPrice", sortable: false },
                    { text: this.$t('mySignals.table.status'), value: "status", align: 'center' },
                    { text: this.$t('mySignals.table.technical'), value: "technical", align: 'center' },
                    { text: this.$t('mySignals.table.fundamental'), value: "fundamental", align: 'center' },
                    { text: this.$t('mySignals.table.strength'), value: "strength", align: 'center' },
                ],
                statusTypes: [
                    { text: this.$t('mySignals.filter.status.pending'), value: "pending" },
                    { text: this.$t('mySignals.filter.status.activeToday'), value: "activeToday" },
                    { text: this.$t('mySignals.filter.status.active'), value: "active" },
                    { text: this.$t('mySignals.filter.status.closed'), value: "closed" },
                ],
                calendarModal: false,
                modalCode: '',
                loadingFavoriteSignals: true,
                mySignals: []
            };
        },
        computed: {
            filters: {
                get() {
                    return this.$store.getters["pageFilters/getObject"]("mySignalsFilters");
                },
                set(newValue) {
                    this.$store.commit("pageFilters/SET_OBJECT", { objectKey: "mySignalsFilters", newObject: newValue });
                },
            },
            getSectors() {
                if (this.mySignals.length == 0) return [];

                return this.filters.sector ? ["", ...new Set(this.mySignals.map(s => s.sector))].sort()
                        : [...new Set(this.mySignals.map(s => s.sector))].sort();
            },
            getStatusTypes() {
                return this.filters.status ? ["", ...new Set(this.statusTypes.map(s => s.text.toUpperCase()))].sort()
                        : [...new Set(this.statusTypes.map(s => s.text.toUpperCase()))].sort();
            },
            filteredItems() {
                if (this.mySignals.length == 0) return [];

                return this.mySignals.filter(item => 
                    // Filtro por ticker y nombre
                    (!this.filters.tickerName || (item.ticker.toLowerCase().includes(this.filters.tickerName.toLowerCase()) || item.name.toLowerCase().includes(this.filters.tickerName.toLowerCase())))
                    // Filtro por sector
                    && (!this.filters.sector || (item.sector.toLowerCase().includes(this.filters.sector.toLowerCase()) ))
                    // Filtro por estado
                    && (!this.filters.status || (this.getStatusName(item).toLowerCase().includes(this.filters.status.toLowerCase())))
                );
            }
        },
        created() {
            // Se muestran los filtros si se accede desde desktop
            this.filtersVisible = this.isMobileDevice() ? false : true;
        },
        mounted() {
            // API calls
            this.getFavoriteSignals();
        },
        methods: {
            // ---- FUNCIONES UTILIZADAS POR LOS COMPONENTES EN PANTALLA  ----
            // Permite mostrar u ocultar las opciones de filtrado
            toggleFilters() {
                this.filtersVisible = !this.filtersVisible;
            },
            // Control para ordenar los resultados en la tabla de señales
            tableSortConfiguration(items, sortBy, descending) {
                if (sortBy.length > 0) {
                    if (sortBy[0] === "technical" || sortBy[0] === "fundamental" || sortBy[0] === "strength") {
                        items.sort((a, b) => {
                            const scoreA = a["indicators"][sortBy[0]]
                            const scoreB = b["indicators"][sortBy[0]]

                            return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                        });
                    } else if (sortBy[0] === "status") {
                        items.sort((a, b) => {
                            const valA = this.getStatusName(a);
                            const valB = this.getStatusName(b);

                            return descending[0] ? valB.toString().localeCompare(valA.toString()) : valA.toString().localeCompare(valB.toString());
                        });
                    } else {
                        items.sort((a, b) => {
                            const valA = a[sortBy[0]];
                            const valB = b[sortBy[0]];

                            if (typeof valA === "number" && typeof valB === "number") {
                                return descending[0] ? valB - valA : valA - valB;
                            } else {
                                return descending[0] ? valB.toString().localeCompare(valA.toString()) : valA.toString().localeCompare(valB.toString());
                            }
                        });
                    }
                }
                return items;
            },
            // Acceso al detalle de una señal
            pushSignalDetailPage(id) {
                this.$router.push({ name: 'SignalDetail', params: { id: id } });
            },
            showErrorModal() {
                this.modalCode = 'apiErrorModal';
                this.showDefaultErrorModal(this.modalCode);
            },
            calculateStatusPrice(item) {
                if (item.finishDate) {
                    return this.calculateGrowthPercentageFormatted(item.price, item.signalPrice);
                } else if (item.breakoutVolume || item.signalPrice < item.price) {
                    return this.calculateGrowthPercentageFormatted(item.price, item.signalPrice);
                } else {
                    return this.calculateGrowthPercentageFormatted(item.price, item.signalPrice, true);
                }
            },
            getStatusName(item) {
                if (item.finishDate) {
                    return this.$t('mySignals.filter.status.closed');
                } else if (item.breakoutVolume || item.signalPrice < item.price) {
                    return (item.breakoutVolume) ? 
                        this.$t('mySignals.filter.status.active') : this.$t('mySignals.filter.status.activeToday');
                } else {
                    return this.$t('mySignals.filter.status.pending');
                }
            },
            // ---- PETICIONES AL BACKEND ----
            // Consulta el listado de señales activas
            async getFavoriteSignals() {
                this.loadingFavoriteSignals = true;
                try {
                    const response = await axios.get('/signal/getFavoriteSignals');
                    this.mySignals = response.data;
                } catch (e) {
                    this.showErrorModal();
                } finally {
                    this.loadingFavoriteSignals = false;
                }
            },
        },
    };
</script>

<style scoped>
    
</style>